<template>
  <div class="parental-page">
    <div class="bg-gradient"></div>
    <div class="orb orb-1"></div>
    <div class="orb orb-2"></div>
    <div class="orb orb-3"></div>

    <div class="content-wrapper">
      <aside class="sidebar">
        <nav class="nav-card">
          <div class="nav-logo">
            <div class="logo-icon">
              <img :src="logo2Image" alt="" class="logo2-icon" />
            </div>
            <div class="brand-copy">
              <h1 class="brand-name">树苗英语</h1>
              <span class="brand-subtitle">TREE SAPLING</span>
            </div>
          </div>

          <ul class="nav-list">
            <li v-for="item in navItems" :key="item.id">
              <a
                href="#"
                @click.prevent="handleNavItemClick(item)"
                :class="['nav-item', { active: activeNav === item.id }]"
              >
                <font-awesome-icon :icon="item.icon" class="nav-icon" />
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>
          <div class="nav-menu-visual" aria-hidden="true">
            <img :src="menuBgImage" alt="" class="nav-menu-image" />
          </div>
        </nav>
      </aside>

      <main class="parental-content">
        <header class="parental-header">
          <button type="button" class="back-btn" @click="handleGoBack" aria-label="返回上一页">
            <font-awesome-icon icon="chevron-left" />
          </button>
          <h1 class="parental-title">家长控制</h1>
          <span class="header-spacer" aria-hidden="true"></span>
        </header>

        <section class="time-control-section" aria-label="时间管控">
          <div class="section-heading">
            <span class="section-heading-icon">
              <font-awesome-icon icon="clock" />
            </span>
            <h2 class="section-heading-title">时间管控</h2>
          </div>

          <div class="settings-panel glass-card">
            <button type="button" class="setting-row" @click="isLimitPickerOpen = true">
              <span class="setting-main">
                <span class="setting-icon">
                  <font-awesome-icon icon="play-circle" />
                </span>
                <span class="setting-title">每日播放时长</span>
              </span>

              <span class="setting-action">
                <span class="setting-value">{{ currentDailyPlayLimitLabel }}</span>
                <font-awesome-icon icon="chevron-right" class="setting-arrow" />
              </span>
            </button>
          </div>
        </section>
      </main>
    </div>

    <Transition name="picker-fade">
      <div v-if="isLimitPickerOpen" class="picker-mask" @click="closeLimitPicker">
        <section class="limit-picker glass-card" @click.stop>
          <header class="picker-header">
            <h2 class="picker-title">每日播放</h2>
            <button
              type="button"
              class="picker-close"
              aria-label="关闭"
              @click="closeLimitPicker"
            >
              <font-awesome-icon icon="xmark" />
            </button>
          </header>

          <div class="limit-options">
            <button
              v-for="option in dailyPlayLimitOptions"
              :key="option.value"
              type="button"
              class="limit-option"
              :class="{ active: dailyPlayLimit === option.value }"
              @click="selectDailyPlayLimit(option.value)"
            >
              <span>{{ option.label }}</span>
              <font-awesome-icon v-if="dailyPlayLimit === option.value" icon="check" />
            </button>
          </div>
        </section>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import logo2Image from '../assets/logo2.png'
import menuBgImage from '../assets/menu_bg.jpg'
import { computed, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { getUser, updateUser } from '@/api/user'

const DAILY_PLAY_LIMIT_STORAGE_KEY = 'tree-sprout-daily-play-limit'
const PARENT_VERIFIED_STORAGE_KEY = 'tree-sprout-parent-verified'
const router = useRouter()
const activeNav = ref('parent')
const navItems = [
  { id: 'home', label: '首页', icon: 'home' },
  { id: 'history', label: '观看历史', icon: 'play-circle' },
  { id: 'favorites', label: '宝贝收藏', icon: 'heart' },
  { id: 'settings', label: '偏好设置', icon: 'cog' },
  { id: 'parent', label: '家长模式', icon: 'user-shield' },
]


const dailyPlayLimitOptions = [
  { value: 'unlimited', label: '不限制' },
  { value: '5', label: '5分钟' },
  { value: '10', label: '10分钟' },
  { value: '15', label: '15分钟' },
  { value: '20', label: '20分钟' },
  { value: '25', label: '25分钟' },
  { value: '30', label: '30分钟' },
  { value: '35', label: '35分钟' },
  { value: '40', label: '40分钟' },
  { value: '45', label: '45分钟' },
  { value: '50', label: '50分钟' },
  { value: '55', label: '55分钟' },
  { value: '60', label: '1小时' },
  { value: '75', label: '1小时15分钟' },
  { value: '90', label: '1小时30分钟' },
  { value: '120', label: '2小时' },
  { value: '180', label: '3小时' },
  { value: '240', label: '4小时' },
]

const getSavedDailyPlayLimit = () => {
  const savedValue = window.localStorage.getItem(DAILY_PLAY_LIMIT_STORAGE_KEY)
  return dailyPlayLimitOptions.some((option) => option.value === savedValue)
    ? savedValue
    : 'unlimited'
}

const dailyPlayLimit = ref(getSavedDailyPlayLimit())
const isLimitPickerOpen = ref(false)

const currentDailyPlayLimitLabel = computed(() => {
  return dailyPlayLimitOptions.find((option) => option.value === dailyPlayLimit.value)?.label || '不限制'
})

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/')
}

const handleNavItemClick = (item) => {
  if (item.id === 'home') {
    router.push('/')
    return
  }

  if (item.id === 'parent') {
    router.push('/parental')
    return
  }

  if (item.id === 'settings') {
    router.push('/preference')
    return
  }

  if (item.id === 'history' || item.id === 'favorites') {
    router.push({
      path: '/history',
      query: item.id === 'favorites' ? { tab: 'favorites' } : {}
    })
  }
}

const closeLimitPicker = () => {
  isLimitPickerOpen.value = false
}

const selectDailyPlayLimit = (value) => {
  updateUser({ dailyPlayLimitMinutes: value })
  dailyPlayLimit.value = value
  window.localStorage.setItem(DAILY_PLAY_LIMIT_STORAGE_KEY, value)
  closeLimitPicker()
}

onMounted(async () => {
  try {
    const user = await getUser()
    window.localStorage.setItem(DAILY_PLAY_LIMIT_STORAGE_KEY, user.dailyPlayLimitMinutes)
  } catch {
    
  }
})
</script>

<style scoped>
.parental-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  color: #2d3436;
  font-family: 'Nunito', 'Source Han Sans CN', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.content-wrapper {
  display: flex;
  height: 100vh;
  position: relative;
  z-index: 10;
}

.sidebar {
  width: 264px;
  flex-shrink: 0;
  height: 100vh;
  overflow: hidden;
  background: #FFF9F5;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.06);
}

.nav-card {
  height: 100%;
  min-height: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 20px;
  border-bottom: 1px solid rgba(255, 138, 61, 0.15);
}

.nav-logo .logo-icon {
  width: 62px;
  height: 62px;
  background: #fff;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(140, 104, 68, 0.12);
}

.nav-logo .logo2-icon {
  display: block;
  width: 52px;
  height: 52px;
  object-fit: contain;
}

.brand-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.nav-logo .brand-name {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: #2D3436;
  letter-spacing: 1px;
  line-height: 1.05;
}

.brand-subtitle {
  color: #a78b76;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.2px;
  line-height: 1;
}

.nav-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: clamp(12px, 2vh, 20px);
  padding: clamp(12px, 2vh, 16px) 12px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: 14px;
  color: #666;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  text-decoration: none;
}

.nav-item:hover {
  background: rgba(255, 138, 61, 0.08);
  color: #FF8A3D;
}

.nav-item.active {
  background: linear-gradient(135deg, #FFE4D4 0%, #FFD4C4 100%);
  color: #FF6B35;
  font-weight: 700;
  box-shadow: 0 4px 16px rgba(255, 138, 61, 0.2);
}

.nav-icon {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

.nav-menu-visual {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  margin-top: auto;
  border-radius: 0;
  overflow: hidden;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.nav-menu-image {
  display: block;
  flex-shrink: 0;
  width: 100%;
  min-width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center bottom;
}

.parental-content {
  flex: 1;
  min-width: 0;
  padding: 24px 40px 40px;
  position: relative;
  z-index: 1;
}

.bg-gradient {
  position: fixed;
  inset: 0;
  z-index: -3;
  background: linear-gradient(135deg, #faf8f5 0%, #f5f0eb 50%, #ede7e0 100%);
}

.orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.5;
  z-index: -2;
  pointer-events: none;
}

.orb-1 {
  width: 500px;
  height: 500px;
  top: -150px;
  right: -100px;
  background: linear-gradient(135deg, #ffe8d6, #ffd4b8);
}

.orb-2 {
  width: 400px;
  height: 400px;
  left: -100px;
  bottom: -100px;
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
}

.orb-3 {
  width: 350px;
  height: 350px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  opacity: 0.3;
}

.glass-card {
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.58);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.82);
}

.parental-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 28px;
  width: min(100%, 580px);
  margin: 0 auto;
}

.parental-brand-row {
  display: flex;
  justify-content: flex-start;
}

@media (max-width: 768px), (min-width: 769px) and (max-width: 1024px) and (orientation: portrait) {
  .parental-brand-row {
    display: none;
  }
}

@media (min-width: 769px) {
  .parental-header {
    display: none;
  }
}

@media (max-width: 1200px) {
  .sidebar {
    width: 224px;
  }
}

@media (max-width: 992px) {
  .sidebar {
    width: 208px;
  }

  .content-wrapper {
    gap: 16px;
  }
}

@media (min-width: 769px) and (max-width: 1200px) {
  .parental-content {
    padding-top: 32px;
  }

  .nav-logo {
    padding: 20px 20px;
    gap: 16px;
  }

  .nav-logo .logo-icon {
    width: 62px;
    height: 62px;
  }

  .nav-logo .logo2-icon {
    width: 52px;
    height: 52px;
  }

  .nav-logo .brand-name {
    font-size: 24px;
  }

  .nav-logo .brand-subtitle {
    font-size: 12px;
  }

  .nav-item {
    padding: 16px;
  }
}

.back-btn {
  position: absolute;
  left: 0;
  top: 50%;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: #5f5044;
  font-size: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  transform: translateY(-50%);
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
}

.back-btn:hover {
  color: #ff8a3d;
  box-shadow: 0 12px 28px rgba(255, 138, 61, 0.14);
}

.header-spacer {
  display: none;
}

.parental-title {
  font-size: 18px;
  line-height: 1.4;
  font-weight: 800;
  margin: 0;
  letter-spacing: 0;
}

.time-control-section {
  width: min(100%, 620px);
  margin: 34px auto 0;
  animation: fadeInUp 0.45s ease-out both;
}

.section-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 34px;
  margin-bottom: 14px;
  color: #2d3436;
}

.section-heading-icon {
  width: 32px;
  height: 32px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ff8a3d;
  font-size: 15px;
  background: linear-gradient(135deg, #fff3e0, #ffe0b2);
  box-shadow: 0 8px 22px rgba(255, 138, 61, 0.18);
}

.section-heading-title {
  margin: 0;
  font-size: 16px;
  line-height: 32px;
  font-weight: 800;
}

.settings-panel {
  min-height: 194px;
  display: flex;
  align-items: center;
  border-radius: 18px;
  overflow: hidden;
  border-color: rgba(95, 80, 68, 0.24);
  box-shadow:
    0 18px 48px rgba(45, 52, 54, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.82);
  position: relative;
}

.settings-panel::before {
  content: '';
  position: absolute;
  width: 180px;
  height: 180px;
  top: -90px;
  right: -54px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 224, 178, 0.74), rgba(255, 224, 178, 0));
  pointer-events: none;
}

.settings-panel::after {
  content: '';
  position: absolute;
  width: 130px;
  height: 130px;
  left: -64px;
  bottom: -68px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(200, 230, 201, 0.62), rgba(200, 230, 201, 0));
  pointer-events: none;
}

.setting-row {
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: 102px;
  border: 0;
  background: transparent;
  padding: 0 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  cursor: pointer;
  color: inherit;
  transition: background 0.22s ease, transform 0.22s ease;
}

.setting-row:hover {
  background: rgba(255, 255, 255, 0.32);
}

.setting-row:active {
  transform: scale(0.995);
}

.setting-row:focus-visible {
  outline: 3px solid rgba(255, 138, 61, 0.35);
  outline-offset: -4px;
}

.setting-main {
  display: inline-flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.setting-icon {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #4caf50;
  font-size: 18px;
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
  box-shadow: 0 10px 26px rgba(76, 175, 80, 0.16);
}

.setting-title {
  font-size: 16px;
  line-height: 1.2;
  font-weight: 800;
  color: #2d3436;
}

.setting-action {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #5f5044;
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
  padding: 10px 12px 10px 16px;
  border-radius: 999px;
  background: rgba(255, 243, 224, 0.82);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
}

.setting-arrow {
  font-size: 13px;
  color: #ff8a3d;
  transition: transform 0.22s ease;
}

.setting-row:hover .setting-arrow {
  transform: translateX(2px);
}

.picker-mask {
  position: fixed;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(45, 52, 54, 0.28);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.limit-picker {
  width: min(100%, 420px);
  max-height: min(620px, calc(100vh - 48px));
  overflow: hidden;
  border-radius: 28px;
}

.picker-header {
  height: 54px;
  padding: 0 16px 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 138, 61, 0.12);
}

.picker-title {
  font-size: 16px;
  font-weight: 800;
  margin: 0;
}

.picker-close {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 243, 224, 0.75);
  color: #ff8a3d;
  cursor: pointer;
}

.picker-close:hover {
  background: rgba(255, 224, 178, 0.85);
}

.limit-options {
  max-height: calc(min(620px, 100vh - 48px) - 54px);
  overflow-y: auto;
  padding: 6px 0;
}

.limit-option {
  width: 100%;
  min-height: 44px;
  border: 0;
  background: transparent;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #2d3436;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

.limit-option:hover,
.limit-option.active {
  background: rgba(255, 243, 224, 0.82);
}

.limit-option.active {
  color: #ff8a3d;
  font-weight: 800;
}

.picker-fade-enter-active,
.picker-fade-leave-active {
  transition: opacity 0.18s ease;
}

.picker-fade-enter-from,
.picker-fade-leave-to {
  opacity: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 640px), (min-width: 641px) and (max-width: 1024px) and (orientation: portrait) {
  .parental-page {
    overflow-y: auto;
  }

  .content-wrapper {
    display: block;
    min-height: 100vh;
  }

  .sidebar {
    display: none;
  }

  .parental-content {
    padding: calc(env(safe-area-inset-top, 0px) + 18px) 16px 24px;
  }

  .parental-header {
    display: flex;
    min-height: 44px;
  }

  .back-btn {
    display: inline-flex;
  }

  .time-control-section {
    margin-top: 22px;
  }

  .settings-panel {
    min-height: 168px;
    border-radius: 18px;
  }

  .setting-row {
    min-height: 96px;
    padding: 0 18px;
    align-items: center;
    flex-direction: row;
    gap: 12px;
  }

  .setting-main {
    gap: 10px;
  }

  .setting-icon {
    width: 38px;
    height: 38px;
    border-radius: 14px;
    font-size: 16px;
  }

  .setting-title {
    font-size: 15px;
  }

  .setting-action {
    align-self: center;
    padding: 8px 10px 8px 12px;
  }

  .picker-mask {
    align-items: flex-end;
    padding: 0;
  }

  .limit-picker {
    width: 100%;
    max-height: 72vh;
    border-radius: 28px 28px 0 0;
  }
}
@media (min-width: 769px) {
  .nav-logo { padding: 20px 20px; gap: 16px; }
  .nav-logo .logo-icon { width: 62px; height: 62px; }
  .nav-logo .logo2-icon { width: 52px; height: 52px; }
  .nav-logo .brand-name { font-size: 24px; letter-spacing: 1px; line-height: 1.05; }
  .nav-logo .brand-subtitle { font-size: 12px; letter-spacing: 1.2px; line-height: 1; }
  .nav-item { padding: clamp(12px, 1.8vh, 18px) 18px; }
}

@media (min-width: 993px) and (max-width: 1200px) {
  .sidebar { width: 224px; }
}

@media (min-width: 769px) and (max-width: 992px) {
  .sidebar { width: 208px; }
}
</style>
